// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Alert, Box, Center, Group, List, Loader, Select, Stack, Text } from '@mantine/core';
import { formatHumanName } from '@medplum/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { type CSSProperties, type JSX, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { PageHeader } from '../../components/layout/PageHeader';
import { getProviderAppEnvConfig } from '../../config/env';
import { useActivePractitioners } from '../../hooks/useActivePractitioners';
import { useProfileCompletion } from '../../hooks/useProfileCompletion';
import { useRole } from '../../hooks/useRole';
import { ProviderAvailabilityV2Section } from '../../scheduling-next/ProviderAvailabilityV2Section';
import { hasPermission } from '../../utils/permissions';
import type { ProfileMissingRequirement } from '../../utils/practitioner';
import { AvailabilitySection } from './sections/AvailabilitySection';
import { CredentialsSection } from './sections/CredentialsSection';
import { LicensesSection } from './sections/LicensesSection';
import { PasswordSection } from './sections/PasswordSection';
import { ProviderInformationSection } from './sections/ProviderInformationSection';
import { ProviderSignatureSection } from './sections/ProviderSignatureSection';
import { ScriptSureProviderSetupSection } from './sections/ScriptSureProviderSetupSection';
import { SystemSettingsSection } from './sections/SystemSettingsSection';

const MISSING_REQUIREMENT_LABELS: Record<ProfileMissingRequirement, string> = {
  'provider-information': 'Complete every field in the Provider Information section.',
};

export function shouldAllowWeeklyAvailabilityEditing(
  crossProvider: boolean,
  isAdmin: boolean,
  canManageProviderAvailability: boolean
): boolean {
  return isAdmin || canManageProviderAvailability || !crossProvider;
}

export function PractitionerSettingsPage(): JSX.Element {
  const { practitioner, setPractitioner, loading, isRequired, isComplete, isFinalizing, missing } =
    useProfileCompletion();
  const { role, isAdmin, loading: roleLoading } = useRole();
  const { practitioners, loading: providersLoading, error: providersError } = useActivePractitioners();
  const { schedulingEngine } = getProviderAppEnvConfig();
  const useV2Availability = schedulingEngine === 'medplum-v2';
  const canManageProviderAvailability = hasPermission({ role, isAdmin, permission: 'manage-provider-availability' });
  const canManagePrescribing = hasPermission({ role, isAdmin, permission: 'prescribe-medications' });
  const [selectedProviderId, setSelectedProviderId] = useState<string>();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (loading) {
      return;
    }
    const hash = location.hash?.replace(/^#/, '');
    if (!hash) {
      return;
    }
    const target = document.getElementById(hash);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, location.hash]);

  const sectionStyle = (section: string) => ({
    opacity: activeSection !== null && activeSection !== section ? 0.5 : 1,
    pointerEvents: (activeSection !== null && activeSection !== section
      ? 'none'
      : 'auto') as CSSProperties['pointerEvents'],
    transition: 'opacity 0.2s',
  });

  const availabilityOptions = useMemo(() => {
    if (!practitioner?.id || practitioners.some((option) => option.value === practitioner.id)) {
      return practitioners;
    }

    return [
      {
        value: practitioner.id,
        label: practitioner.name?.[0] ? formatHumanName(practitioner.name[0]) : 'Your availability',
        practitioner,
      },
      ...practitioners,
    ];
  }, [practitioner, practitioners]);

  useEffect(() => {
    if (!canManageProviderAvailability || selectedProviderId || availabilityOptions.length === 0) {
      return;
    }
    setSelectedProviderId(
      availabilityOptions.find((option) => option.value === practitioner?.id)?.value ?? availabilityOptions[0]?.value
    );
  }, [availabilityOptions, canManageProviderAvailability, practitioner?.id, selectedProviderId]);

  if (loading) {
    return (
      <Center mt="xl">
        <Loader />
      </Center>
    );
  }

  if (!practitioner) {
    return (
      <Center mt="xl">
        <Text c="dimmed">Could not load practitioner profile.</Text>
      </Center>
    );
  }

  const showOnboarding = isRequired && !isComplete;
  const selectedProvider = availabilityOptions.find((option) => option.value === selectedProviderId)?.practitioner;
  const availabilityPractitioner = canManageProviderAvailability ? selectedProvider : practitioner;
  const crossProvider = Boolean(availabilityPractitioner?.id && availabilityPractitioner.id !== practitioner.id);
  const renderAvailability = (): JSX.Element => {
    if (showOnboarding) {
      return (
        <Alert color="blue" title="Availability setup comes after onboarding">
          Once your provider information is complete, your account will be activated and you can configure your
          scheduling availability.
        </Alert>
      );
    }

    if (useV2Availability) {
      if (canManageProviderAvailability && providersLoading && !availabilityPractitioner) {
        return <Loader />;
      }
      if (canManageProviderAvailability && !availabilityPractitioner) {
        return <Alert color="yellow">No active schedulable providers are available.</Alert>;
      }
      return (
        <ProviderAvailabilityV2Section
          practitioner={availabilityPractitioner ?? practitioner}
          allowWeeklyEditing={shouldAllowWeeklyAvailabilityEditing(
            crossProvider,
            isAdmin,
            canManageProviderAvailability
          )}
          isEditing={activeSection === 'availability'}
          onStartEdit={() => setActiveSection('availability')}
          onEndEdit={() => setActiveSection(null)}
          onSaved={crossProvider ? undefined : setPractitioner}
        />
      );
    }

    return (
      <AvailabilitySection
        isEditing={activeSection === 'availability'}
        onStartEdit={() => setActiveSection('availability')}
        onEndEdit={() => setActiveSection(null)}
      />
    );
  };

  return (
    <Stack gap="md" p="md">
      <PageHeader title="Account Settings" />
      {showOnboarding && (
        <Alert
          variant="light"
          color="yellow"
          icon={isFinalizing ? <Loader size="xs" /> : <IconAlertTriangle size={20} />}
          title={isFinalizing ? 'Finalizing your account…' : 'Finish setting up your account'}
        >
          {isFinalizing ? (
            <Group gap="xs">
              <Text size="sm">Activating your full access — this should only take a moment.</Text>
            </Group>
          ) : (
            <>
              <Text size="sm" mb="xs">
                Before you can start seeing patients, please complete the items below.
              </Text>
              <List size="sm" withPadding>
                {missing.map((item) => (
                  <List.Item key={item}>{MISSING_REQUIREMENT_LABELS[item]}</List.Item>
                ))}
              </List>
            </>
          )}
        </Alert>
      )}
      <Box style={sectionStyle('provider')}>
        <ProviderInformationSection
          practitioner={practitioner}
          onSaved={setPractitioner}
          isEditing={activeSection === 'provider'}
          onStartEdit={() => setActiveSection('provider')}
          onEndEdit={() => setActiveSection(null)}
        />
      </Box>
      <Box style={sectionStyle('system')}>
        <SystemSettingsSection
          practitioner={practitioner}
          onSaved={setPractitioner}
          isEditing={activeSection === 'system'}
          onStartEdit={() => setActiveSection('system')}
          onEndEdit={() => setActiveSection(null)}
        />
      </Box>
      <Box style={sectionStyle('signature')}>
        <ProviderSignatureSection practitioner={practitioner} onSaved={setPractitioner} />
      </Box>
      <Box style={sectionStyle('password')}>
        <PasswordSection practitioner={practitioner} />
      </Box>
      {canManagePrescribing && (
        <Box id="prescribing-setup" style={sectionStyle('prescribing-setup')}>
          <ScriptSureProviderSetupSection practitioner={practitioner} />
        </Box>
      )}
      {useV2Availability && canManageProviderAvailability && (
        <Box>
          <Select
            label="Provider availability"
            description="Select a provider to manage their v2 availability and time-off blocks."
            placeholder="Select a provider"
            data={availabilityOptions.map((option) => ({ value: option.value, label: option.label }))}
            value={selectedProviderId ?? null}
            onChange={(value) => setSelectedProviderId(value ?? undefined)}
            searchable
            clearable={false}
            disabled={providersLoading || roleLoading}
            error={providersError?.message}
          />
        </Box>
      )}
      {role !== 'clinical-ops-rcm' && (
        <Box id="availability" style={sectionStyle('availability')}>
          {renderAvailability()}
        </Box>
      )}
      {role !== 'clinical-ops-rcm' && (
        <Box style={sectionStyle('licenses')}>
          <LicensesSection
            practitioner={practitioner}
            qualifications={practitioner.qualification ?? []}
            onSaved={setPractitioner}
          />
        </Box>
      )}
      {role !== 'clinical-ops-rcm' && (
        <Box style={sectionStyle('credentials')}>
          <CredentialsSection
            practitioner={practitioner}
            qualifications={practitioner.qualification ?? []}
            onSaved={setPractitioner}
          />
        </Box>
      )}
    </Stack>
  );
}
